<template>
  <div
    v-click-outside="closeSavings"
    class="quantity-steps"
    :class="myTemplate"
  >
    <div class="quantity-steps__inner">
      <button
        :class="{
          'quantity-steps__button': true,
          'quantity-steps__button--hover': stepDownHover,
          'app-view': isAppView,
        }"
        :disabled="disabled || stepDown === null || isNaN(stepDown) || isLoading === true"
        data-test="quantity-steps-minus"
        @click.stop.prevent="quantityStepMinus()"
        @mouseover="stepDownHover = true"
        @mouseout="stepDownHover = false"
      >
        <icon-fi
          class="quantity-steps__button__icon"
          icon="minus"
          :size="large ? '1' : '0_8'"
        />
      </button>

      <div
        v-if="disabled && disabledText"
        class="quantity-steps__inner__disabled-text"
      >
        <span>{{ disabledText }}</span>
      </div>

      <text-input-fi
        v-else
        id="stk-prefix"
        ref="input"
        data-test="stk-prefix"
        :model-value="quantityInputValue"
        :class="{
          'quantity-steps__input': true,
          'text-input-fi--light text-input-fi--focus-dark': template === 'addArticle',
          'text-input-fi--light': light,
          'quantity-steps__input--auto-width': autoWidth,
        }"
        :small="small && !large"
        :large="large"
        :disabled="inputDisabled"
        :prefix="prefix"
        :select-input-on-focus="selectInputOnFocus"
        arrow-key-control-enabled
        @update:model-value="updateQuantityInputValue"
        @keyup-enter="onKeyUpEnter"
        @keydown-arrow-up="onArrowKeyUp"
        @keydown-arrow-down="onArrowKeyDown"
        @focus="quantityFocused"
        @blur="onBlur"
      />

      <button
        :class="{
          'quantity-steps__button': true,
          'quantity-steps__button--hover': stepUpHover,
          'app-view': isAppView,
        }"
        :disabled="disabled || stepUp === null || isNaN(stepUp) || isLoading === true"
        data-test="quantity-steps-plus"
        @click.stop.prevent="quantityStepPlus()"
        @mouseover="stepUpHover = true"
        @mouseout="stepUpHover = false"
      >
        <icon-fi
          class="quantity-steps__button__icon"
          icon="plus"
          :size="large ? '1' : '0_8'"
        />
      </button>
    </div>

    <div
      v-if="priceRowsSavings"
      class="quantity-steps__savings"
    >
      <div class="quantity-steps__savings__btn">
        <link-fi
          :href="'#'"
          :size="isMD ? 18 : 16"
          primary
          @click.prevent="openSavings"
        >
          {{ $dict.get('online-Shop.Product.Show-Price-Rows-Savings') }}
        </link-fi>
      </div>

      <price-rows-savings-fi
        v-if="showSavings && isMD"
        :savings="priceRowsSavings"
        class="quantity-steps__price-rows-savings"
        @clicked="(item) => changeQuantity(item.minQty)"
      />
    </div>
  </div>
</template>

<script>
import { IconFi, LinkFi, TextInputFi } from 'atoms';
import vClickOutside from 'click-outside-vue3';
import PriceRowsSavingsFi from 'components/price-rows-savings-fi/PriceRowsSavingsFi';
import breakpointMixin from 'mixins/breakpointMixin';
import numberFormatMixin from 'mixins/numberFormatMixin';
import qantityStepperPartialsMixin from 'mixins/quantityStepperPartialsMixin';
import sellingUnitMixin from 'mixins/sellingUnitMixin';
import { debounce } from 'throttle-debounce';

const maxInputValue = 990000;
const isInRange = (value, stepRange) => {
  if (Number.isNaN(value)) {
    return false;
  }

  const fitLowerBound = stepRange.rangeFrom !== null ? value >= stepRange.rangeFrom : true;
  const upperBound = stepRange.rangeTo !== null ? stepRange.rangeTo : maxInputValue;
  const fitUpperBound = value <= upperBound;

  return fitLowerBound && fitUpperBound;
};
const isInRemainingStock = (value, stepRange) => {
  if (Number.isNaN(value)) {
    return false;
  }
  return value <= stepRange.rangeTo;
};
const isInStep = (value, stepRange) => value % stepRange.rangeValue === 0;
const stepDownRange = (valueParam, stepRange) => {
  if (stepRange.rangeValue === null && stepRange.rangeFrom === null && stepRange.rangeTo === null) {
    return null;
  }
  let value = valueParam;
  const rangeTo = stepRange.rangeTo || maxInputValue;
  const rangeFrom = stepRange.rangeFrom || 0;
  if (value <= rangeFrom) {
    return undefined;
  }
  const offset = value % stepRange.rangeValue;
  if (offset) {
    value -= offset;
  } else {
    value -= stepRange.rangeValue;
  }
  let steppedValue = Math.max(value, rangeFrom);
  if (steppedValue > rangeTo) {
    steppedValue = rangeTo; // makes sure we have a value even when there are gaps in the ranges
  }
  if (steppedValue === 0) {
    return undefined;
  }
  return steppedValue;
};
const stepUpRange = (value, stepRange) => {
  if (stepRange.rangeValue === null && stepRange.rangeFrom === null && stepRange.rangeTo === null) {
    return null;
  }
  const rangeTo = stepRange.rangeTo || maxInputValue;
  const rangeFrom = stepRange.rangeFrom || 0;
  if (value >= rangeTo) {
    return undefined;
  }
  let steppedValue = value - (value % stepRange.rangeValue) + stepRange.rangeValue;
  if (steppedValue < rangeFrom) {
    steppedValue = rangeFrom; // makes sure we have a value even when there are gaps in the ranges
  }
  return Math.min(steppedValue, rangeTo);
};

export default {
  name: 'QuantitySteps',
  components: {
    IconFi,
    TextInputFi,
    LinkFi,
    PriceRowsSavingsFi,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  mixins: [sellingUnitMixin, breakpointMixin, numberFormatMixin, qantityStepperPartialsMixin],
  props: {
    item: {
      type: Object,
      default: null,
    },
    template: {
      default: 'article',
      type: String,
    },
    requestedQuantity: {
      type: Number,
      default: null,
    },
    noBtnMargins: {
      type: Boolean,
    },
    isLoading: Boolean,
    disabled: {
      default: false,
      type: Boolean,
    },
    disabledText: {
      default: '',
      type: String,
    },
    productId: {
      type: [String, Number],
      default: null,
    },
    light: Boolean,
    small: {
      default: false,
      type: Boolean,
    },
    large: {
      default: false,
      type: Boolean,
    },
    priceRowsSavings: {
      type: Array,
      default: null,
    },
    selectInputOnFocus: {
      type: Boolean,
      default: false,
    },
    arrowKeyControlEnabled: {
      type: Boolean,
      default: false,
    },
    triggerRemoveEventOnZero: {
      type: Boolean,
      default: false,
    },
    autoWidth: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'arrow-key-pressed',
    'blur',
    'focus',
    'keyup-enter',
    'quantity-input',
    'quantity-stepper-remove-item',
    'scrollToElement',
    'text-input',
    'update-quantity',
    'user-input',
    'value-rounded',
  ],
  data() {
    return {
      // Inputfield quantity value
      quantityInputValue: '1',

      // Helper vars
      maxInputValue,
      isClicked: undefined,
      timeOut: null,
      stepDownHover: false,
      stepUpHover: false,
      showSavings: false,
    };
  },
  computed: {
    allowPartials() {
      return this.allowQuantityPartials(this.item);
    },
    myTemplate() {
      const classes = [];
      if (this.template) {
        classes.push(`quantity-steps--${this.template}`);
      }
      if (this.light) {
        classes.push('quantity-steps--light');
      }
      if (this.small && !this.large) {
        classes.push('quantity-steps--small');
      }
      if (this.large) {
        classes.push('quantity-steps--large');
      }
      return classes;
    },
    stepRanges() {
      return this.item?.quantityStepRanges || [{}];
    },
    prefix() {
      return this.item?.unit?.code
        ? this.getSellingUnit(this.item.unit.code)
        : this.getSellingUnit('ST');
    },
    inputDisabled() {
      if (this.disabled) {
        return true;
      }

      if (this.stepRanges === null || this.stepRanges.length === 0) {
        return true;
      }

      if (this.stepRanges.length === 1) {
        const stepRange = this.stepRanges[0];
        if (
          stepRange.rangeFrom === null &&
          stepRange.rangeTo === null &&
          stepRange.rangeValue === null
        ) {
          return true;
        }
      }

      return false;
    },
    theme() {
      return this.template === 'article' ? 'theme--blocky' : 'theme--blocky-dark';
    },
    buttonClass() {
      return [this.theme, 'ma-0', 'quantity-steps__buttons'];
    },
    inputValue() {
      return parseInt(this.getRawValue(this.quantityInputValue), 10);
    },
    stepUp() {
      let newQuantity;
      this.stepRanges.forEach((stepRange) => {
        const steppedValue = stepUpRange(this.inputValue, stepRange);
        if (!Number.isNaN(steppedValue)) {
          if (!(steppedValue > newQuantity)) {
            // true if newQuantity is NaN
            newQuantity = steppedValue;
          }
        }
      });
      if (newQuantity === undefined) {
        newQuantity = null;
      }
      return newQuantity;
    },
    stepDown() {
      let newQuantity;
      this.stepRanges.forEach((stepRange) => {
        const steppedValue = stepDownRange(this.inputValue, stepRange);
        if (!Number.isNaN(steppedValue)) {
          if (!(steppedValue < newQuantity)) {
            // true if newQuantity is NaN
            newQuantity = steppedValue;
          }
        }
      });
      if (newQuantity === undefined) {
        newQuantity = null;
      }
      return newQuantity;
    },
  },
  watch: {
    requestedQuantity(value) {
      this.quantityInputValue = this.getFormattedValue(value);
    },
    item: {
      deep: true,
      handler(item, oldItem) {
        if (!item) {
          return;
        }
        if (oldItem && oldItem.articleNo === item.aricleNo) {
          return;
        }
        const quantityInputValueNumber =
          this.requestedQuantity ||
          (item && (item.quantity || item.requestedQuantity || item.initialQuantity)) ||
          1;

        this.quantityInputValue = this.getFormattedValue(quantityInputValueNumber);
      },
      immediate: true,
    },
  },
  methods: {
    focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    updateQuantityInputValue(value) {
      if (!this.debounceInput) {
        this.debounceInput = debounce(600, () => {
          const currentValue = this.getRawValue(this.quantityInputValue);
          if (currentValue > 0) {
            if (
              this.allowPartials ||
              this.stepRanges.some(
                (stepRange) =>
                  isInStep(currentValue, stepRange) && isInRange(currentValue, stepRange),
              )
            ) {
              this.$emit('user-input', currentValue);
            }
          }
        });
      }
      this.quantityInputValue = this.getFormattedValue(value);
      this.$emit('user-input');
      this.debounceInput();
    },
    onKeyUpEnter(value) {
      this.quantityStepInputField(value, false);
      this.$emit('keyup-enter');
    },
    onBlur(value, event) {
      this.quantityStepInputField(value, true);
      this.$emit('blur', value, event);
    },
    getInputValue() {
      return this.inputValue;
    },
    quantityStepPlus() {
      if (this.isAppView) {
        this.stepUpHover = false;
      }
      this.$emit('quantity-input');
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }

      if (this.stepUp !== null) {
        this.quantityInputValue = this.getFormattedValue(this.stepUp);
        this.setTimeOut();
      }
    },
    quantityStepMinus() {
      if (this.isAppView) {
        this.stepDownHover = false;
      }
      this.$emit('quantity-input');
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }

      if (this.stepDown !== null) {
        this.quantityInputValue = this.getFormattedValue(this.stepDown);
        this.setTimeOut();
      }
    },
    triggerInput() {
      this.quantityStepInputField(this.quantityInputValue, true);
    },
    quantityStepInputField(newValue, updateOnlyOnModification) {
      if (this.triggerRemoveEventOnZero && parseInt(newValue, 10) === 0) {
        this.$emit('quantity-stepper-remove-item', this.item.articleNo);
        return;
      }

      // remove all non numeric characters
      let quantityInputValueNumber;
      if (newValue.target) {
        quantityInputValueNumber = this.getRawValue(newValue.target.value);
      } else {
        quantityInputValueNumber = this.getRawValue(newValue);
      }

      if (quantityInputValueNumber > maxInputValue) {
        quantityInputValueNumber = this.getFormattedValue(maxInputValue);
      }

      this.quantityInputValue = String(quantityInputValueNumber);
      if (!this.quantityInputValue.length) {
        this.quantityInputValue = '0';
      }

      // Check if old value is equal to new value or new value is equal to initialQuantity (first request)
      const requestedQuantity = parseInt(this.quantityInputValue, 10);
      let isValidQuantity;
      let isOutOfRemainingStock;
      if (requestedQuantity > 0) {
        // false for NaN
        if (this.allowPartials) {
          isValidQuantity = true;
        } else if (requestedQuantity < this.item.initialQuantity) {
          this.quantityInputValue = this.getFormattedValue(this.item.initialQuantity);
          this.$emit('update-quantity', this.quantityInputValue, this.productId);
          this.$emit('value-rounded', this.$dict.get('online-Shop.Product.Min-Quantity', [this.item.initialQuantity]));
          return;
        } else {
          for (let i = 0; i < this.stepRanges.length; i += 1) {
            const stepRange = this.stepRanges[i];
            if (isInStep(requestedQuantity, stepRange) && isInRange(requestedQuantity, stepRange)) {
              isValidQuantity = true;
              this.quantityInputValue = this.getFormattedValue(this.quantityInputValue);
              break;
            } else if (
              this.item.articleStatus === 'REMAINING_STOCK' &&
              !isInRemainingStock(requestedQuantity, stepRange)
            ) {
              isOutOfRemainingStock = true;
              this.quantityInputValue = this.getFormattedValue(stepRange.rangeTo);
              break;
            }
          }
        }
        if (isValidQuantity) {
          const inputRawValue = this.getRawValue(this.quantityInputValue);
          if (
            updateOnlyOnModification &&
            (inputRawValue === this.item.quantity ||
              inputRawValue === String(this.item.requestedQuantity))
          ) {
            return;
          }
          this.sendNewQuantityToParent();
        } else if (isOutOfRemainingStock) {
          this.$emit('update-quantity', this.quantityInputValue, this.productId);
          this.$emit('value-rounded', this.$dict.get('online-Shop.Product.Out-Of-Remaining-Stock'));
        } else {
          this.roundValue();
        }
      } else {
        this.roundValue();
      }
      this.quantityInputValue = this.getFormattedValue(this.quantityInputValue);
    },
    changeQuantity(quantity) {
      this.closeSavings();
      this.quantityInputValue = quantity;
      this.sendNewQuantityToParent();
      this.quantityInputValue = this.getFormattedValue(quantity);
    },
    setTimeOut() {
      this.timeOut = setTimeout(() => {
        this.sendNewQuantityToParent();
        this.timeOut = null;
      }, 1000);
    },
    sendNewQuantityToParent() {
      const currentInputValue = this.getRawValue(this.quantityInputValue);
      const newQuantity =
        typeof currentInputValue === 'string' ? parseInt(currentInputValue, 10) : currentInputValue;
      this.$emit('update-quantity', newQuantity, this.productId);
    },
    roundValue() {
      if (this.stepRanges?.length) {
        const stepRangeValue = this.stepRanges[0].rangeValue;
        const factor = Math.ceil(this.getRawValue(this.quantityInputValue) / stepRangeValue);

        let quantityInputValueNumber = stepRangeValue * factor;

        if (quantityInputValueNumber < stepRangeValue) {
          quantityInputValueNumber = stepRangeValue;
        }
        this.quantityInputValue = String(quantityInputValueNumber);

        this.$emit('update-quantity', this.quantityInputValue, this.productId);
        this.$emit(
          'value-rounded',
          this.$dict.get('online-Shop.Product.Quantity-Rounded-Up', [stepRangeValue]),
          'rounded-up',
        );
      }
    },
    quantityFocused() {
      if (this.isMD) {
        this.openSavings();
      }

      this.$emit('focus');
    },
    openSavings() {
      if (this.isMD) {
        this.showSavings = true;
      } else {
        this.$emit('scrollToElement');
      }
    },
    closeSavings() {
      this.showSavings = false;
    },
    onArrowKeyUp() {
      if (!this.arrowKeyControlEnabled) {
        return;
      }

      this.quantityInputValue = this.getFormattedValue(this.stepUp);
      this.$emit('arrow-key-pressed');
    },
    onArrowKeyDown() {
      if (!this.arrowKeyControlEnabled) {
        return;
      }

      this.quantityInputValue = this.getFormattedValue(this.stepDown);
      this.$emit('arrow-key-pressed');
    },
  },
};
</script>

<style lang="scss">
@import './quantity-steps';
</style>
